import { FormSection } from '@ksyos/design-system';
import { useEffect } from 'react';
import type { OsasTrialPeriod } from '@common/enums';
import type { TreatmentData } from '@common/validation/customControls/osas/osasAllTreatmentsSchemas/types';
import type { TreatmentChoice } from '@common/validation/customControls/osas/osasEvaluationSchemas/types';
import type { OsasFollowUpConsultCustomControlData } from '@common/validation/customControls/osas/osasFollowUpConsultSchema';
import type { ValidationErrors } from '@frontend/utils';
import { BooleanRadioGroupFormGroup, TextAreaFormGroup } from '../../../processActivityFormGroupFields';
import { TreatmentChoiceFormSection } from '../../osasEvaluation/components/TreatmentChoiceFormSection';
import { TrialPeriodFormSection } from '../../osasEvaluation/components/TrialPeriodFormSection';
import { calculateTreatmentChoice } from '../../osasEvaluation/utils/treatmentChoiceUtils';

type CurrentPolicyFormSectionProps = {
    data: OsasFollowUpConsultCustomControlData;
    errors: ValidationErrors;
    hasInProgressTreatments: boolean;
    inProgressTreatments?: TreatmentData;
    onChange: (
        data: Partial<OsasFollowUpConsultCustomControlData>,
        validateKeys?: string | string[] | undefined
    ) => void;
    onChangeTrialPeriod: (reatmentsToChange: TreatmentChoice, trialPeriod: OsasTrialPeriod) => void;
};

export function CurrentPolicyFormSection(props: Readonly<CurrentPolicyFormSectionProps>) {
    const { data, hasInProgressTreatments, inProgressTreatments, onChange, onChangeTrialPeriod } = props;
    const hasTrialPeriodFormSection = data.treatmentChoice.PAP || data.treatmentChoice.SPT;

    useEffect(() => {
        if (!inProgressTreatments) {
            return;
        }

        if (Object.values(data.treatmentChoice).some((value) => !!value)) {
            return;
        }

        onChange({ treatmentChoice: calculateTreatmentChoice(inProgressTreatments) });
    }, [inProgressTreatments]);

    return (
        <FormSection title="Huidig beleid">
            <TreatmentChoiceFormSection
                hasTreatmentSections={false}
                onChangeTrialPeriod={onChangeTrialPeriod}
            />
            <BooleanRadioGroupFormGroup
                fieldKey="isTreatmentStarted"
                isRequired={hasInProgressTreatments}
                label="Behandeling gestart"
                type="radio"
                value={data.isTreatmentStarted}
            />
            {hasTrialPeriodFormSection && (
                <TrialPeriodFormSection
                    isRequired={true}
                    onChangeTrialPeriod={onChangeTrialPeriod}
                />
            )}

            <TextAreaFormGroup
                fieldKey="taskForPoliclinic"
                label="Taak voor de Ksyos poli"
            />
        </FormSection>
    );
}
